import React, { useState, useEffect, useRef } from 'react'
import { getServerSessionId } from '../../lib/serverSession'

interface TeamMember {
  id: number
  first_name: string
  last_name: string
  title: string
  photo_url: string | null
  description: string | null
  linkedin_url: string | null
  twitter_url: string | null
  facebook_url: string | null
  instagram_url: string | null
  email: string | null
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface TeamManagerProps {
  currentUser?: any
}

export const TeamManager: React.FC<TeamManagerProps> = () => {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    photo_url: '',
    description: '',
    linkedin_url: '',
    twitter_url: '',
    facebook_url: '',
    instagram_url: '',
    email: '',
    display_order: 0,
    is_active: true
  })

  useEffect(() => {
    loadMembers()
  }, [])

  const loadMembers = async () => {
    setLoading(true)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_members' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setMembers(result.members || [])
    } catch (error: any) {
      console.error('Error loading team members:', error)
      setMessage({ type: 'error', text: error.message })
    } finally {
      setLoading(false)
    }
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' })
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 5MB' })
      return
    }

    setUploadingPhoto(true)
    try {
      const fileName = `team-${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`

      // Convert file to base64
      const reader = new FileReader()
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1]

        const sessionId = getServerSessionId()
        const response = await fetch('/api/admin/team', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionId ? { 'X-Session-Id': sessionId } : {})
          },
          body: JSON.stringify({
            operation: 'upload_photo',
            data: { fileName, fileData: base64, contentType: file.type }
          })
        })
        const result = await response.json()
        if (!result.success) throw new Error(result.error)

        setFormData(prev => ({ ...prev, photo_url: result.publicUrl }))
        setMessage({ type: 'success', text: 'Photo uploaded successfully' })
        setUploadingPhoto(false)
      }
      reader.onerror = () => {
        throw new Error('Failed to read file')
      }
      reader.readAsDataURL(file)
    } catch (error: any) {
      console.error('Error uploading photo:', error)
      setMessage({ type: 'error', text: error.message })
      setUploadingPhoto(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.first_name || !formData.last_name || !formData.title) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' })
      return
    }

    setSaving(true)
    try {
      const memberData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        title: formData.title,
        photo_url: formData.photo_url || null,
        description: formData.description || null,
        linkedin_url: formData.linkedin_url || null,
        twitter_url: formData.twitter_url || null,
        facebook_url: formData.facebook_url || null,
        instagram_url: formData.instagram_url || null,
        email: formData.email || null,
        display_order: formData.display_order,
        is_active: formData.is_active
      }

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_member',
          data: { memberId: editingMember?.id, memberData }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMessage({ type: 'success', text: editingMember ? 'Team member updated successfully' : 'Team member added successfully' })
      resetForm()
      loadMembers()
    } catch (error: any) {
      console.error('Error saving team member:', error)
      setMessage({ type: 'error', text: error.message })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member)
    setFormData({
      first_name: member.first_name,
      last_name: member.last_name,
      title: member.title,
      photo_url: member.photo_url || '',
      description: member.description || '',
      linkedin_url: member.linkedin_url || '',
      twitter_url: member.twitter_url || '',
      facebook_url: member.facebook_url || '',
      instagram_url: member.instagram_url || '',
      email: member.email || '',
      display_order: member.display_order,
      is_active: member.is_active
    })
    setShowForm(true)
  }

  const handleDelete = async (member: TeamMember) => {
    if (!confirm(`Delete ${member.first_name} ${member.last_name}?`)) return

    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'delete_member', data: { memberId: member.id } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setMessage({ type: 'success', text: 'Team member deleted' })
      loadMembers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message })
    }
  }

  const toggleActive = async (member: TeamMember) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'toggle_active', data: { memberId: member.id, isActive: member.is_active } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      loadMembers()
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message })
    }
  }

  const resetForm = () => {
    setEditingMember(null)
    setShowForm(false)
    setFormData({
      first_name: '',
      last_name: '',
      title: '',
      photo_url: '',
      description: '',
      linkedin_url: '',
      twitter_url: '',
      facebook_url: '',
      instagram_url: '',
      email: '',
      display_order: members.length,
      is_active: true
    })
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#ffffff' }}>
        Loading team members...
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
          👥 Team Management
        </h2>
        <button
          onClick={() => { resetForm(); setShowForm(true) }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          + Add Team Member
        </button>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '8px',
          backgroundColor: message.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
          border: `1px solid ${message.type === 'error' ? '#EF4444' : '#10B981'}`,
          color: message.type === 'error' ? '#EF4444' : '#10B981'
        }}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div style={{
          backgroundColor: '#1E1E1E',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #333'
        }}>
          <h3 style={{ color: '#FFD700', marginBottom: '16px' }}>
            {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#fff'
                  }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#fff'
                  }}
                  required
                />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                Title / Position *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Chief Executive Officer"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#121212',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff'
                }}
                required
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                Photo
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {formData.photo_url && (
                  <img
                    src={formData.photo_url}
                    alt="Preview"
                    style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                )}
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      opacity: uploadingPhoto ? 0.7 : 1
                    }}
                  >
                    {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                  </button>
                </div>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                Bio / Description
              </label>
              <textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the team member's background and role..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#121212',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#FFD700', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Social Media Links
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                    LinkedIn URL
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={e => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/in/..."
                    style={{ width: '100%', padding: '8px', backgroundColor: '#121212', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                    Twitter / X URL
                  </label>
                  <input
                    type="url"
                    value={formData.twitter_url}
                    onChange={e => setFormData(prev => ({ ...prev, twitter_url: e.target.value }))}
                    placeholder="https://twitter.com/..."
                    style={{ width: '100%', padding: '8px', backgroundColor: '#121212', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    value={formData.facebook_url}
                    onChange={e => setFormData(prev => ({ ...prev, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/..."
                    style={{ width: '100%', padding: '8px', backgroundColor: '#121212', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    value={formData.instagram_url}
                    onChange={e => setFormData(prev => ({ ...prev, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/..."
                    style={{ width: '100%', padding: '8px', backgroundColor: '#121212', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                Contact Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#121212',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff'
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#ffffff', marginBottom: '4px', fontSize: '14px' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={e => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#fff'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ color: '#ffffff' }}>Active (visible on homepage)</span>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : (editingMember ? 'Update' : 'Add Member')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
      <div style={{ display: 'grid', gap: '16px' }}>
        {members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#ffffff', backgroundColor: '#1E1E1E', borderRadius: '12px' }}>
            No team members yet. Click "Add Team Member" to get started.
          </div>
        ) : (
          members.map(member => (
            <div
              key={member.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px',
                backgroundColor: '#1E1E1E',
                borderRadius: '12px',
                border: `1px solid ${member.is_active ? '#333' : '#7F1D1D'}`,
                opacity: member.is_active ? 1 : 0.6
              }}
            >
              {member.photo_url ? (
                <img
                  src={member.photo_url}
                  alt={`${member.first_name} ${member.last_name}`}
                  style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  color: '#ffffff'
                }}>
                  {member.first_name[0]}{member.last_name[0]}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ color: '#FFFFFF', fontWeight: '600', fontSize: '16px' }}>
                  {member.first_name} {member.last_name}
                </div>
                <div style={{ color: '#FFD700', fontSize: '14px' }}>{member.title}</div>
                <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                  Order: {member.display_order} | {member.is_active ? '✅ Active' : '❌ Inactive'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => toggleActive(member)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: member.is_active ? '#7F1D1D' : '#065F46',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {member.is_active ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => handleEdit(member)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(member)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#DC2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
